import { queryClient, useClient } from '@weenat/client'
import { OrgOption, useAllUsersOrgs } from '@weenat/client/dist/hooks/useAllUsersOrgs'
import { Org, RawParentNetwork } from '@weenat/client/dist/resources/orgs'
import { AbsoluteHref } from '@weenat/client/dist/routx/runtime-core'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { useMatch, useNavigate, useParams, usePathname, useSearchParams } from 'app/routx-router'
import useDisclosure from 'app/src/hooks/useDisclosure'
import InvisibleTextInput from 'app/src/kit/fields/InvisibleInput'
import Icons from 'app/src/kit/Icons'
import { LogoSphere } from 'app/src/kit/icons/logos'
import LoadingCircle from 'app/src/kit/loaders/LoadingCircle'
import Modal from 'app/src/kit/Modal'
import Text from 'app/src/kit/Text'
import { useToken } from 'app/state'
import { isEmpty, isNil } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { css, styled } from 'styled-components'
import FarmsList from './FarmsList'

const FieldContainer = styled(Flex)<{ $isFarmInNetwork: boolean; $isInteractive?: boolean }>`
  flex-direction: column;
  margin-top: ${(p) => p.theme.spacings.lg}px;
  margin-bottom: ${(p) => p.theme.spacings.sm}px;
  border-radius: ${(p) => p.theme.radiuses.md}px;

  user-select: none;

  ${(p) =>
    p.$isInteractive
      ? css`
          &:hover {
            border-color: ${(p) => p.theme.colors.primary['500']};
            cursor: pointer;
            background-color: ${(p) => p.theme.colors.primary['200']};
          }
        `
      : null}
`

const NetworkLogo = styled.img`
  width: 24px;
  height: fit-content;
  max-height: 24px;
`

const OrgText = styled(Text)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-weight: ${(p) => p.theme.typography.weights.medium};
`

const SearchField = styled(Flex)`
  position: sticky;
  padding: ${(p) => p.theme.spacings.md}px;
  background: ${(p) => p.theme.colors.grayscale['50']};
  border-bottom: ${(p) => p.theme.borderWidths.sm}px solid ${(p) => p.theme.colors.grayscale['300']};
  top: 0px;
  z-index: 1;
  & input {
    font-size: 16px;
  }
  height: 48px;
`

const SelectOrgField = styled(Flex)`
  height: 40px;
  border-radius: ${(p) => p.theme.radiuses.md}px;
  border: ${(p) => p.theme.borderWidths.sm}px solid ${(p) => p.theme.colors.grayscale['300']};
  padding: ${(p) => p.theme.spacings.md}px;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacings.md}px;
  &:first-child:not(:last-child) {
    border-bottom: 0px;
    border-bottom-left-radius: ${(p) => p.theme.radiuses.none}px;
    border-bottom-right-radius: ${(p) => p.theme.radiuses.none}px;
  }
  &:last-child:not(:first-child) {
    border-top-left-radius: ${(p) => p.theme.radiuses.none}px;
    border-top-right-radius: ${(p) => p.theme.radiuses.none}px;
  }
  & > div:first-child {
    gap: ${(p) => p.theme.spacings.md}px;
    align-items: center;
    & > div:first-child {
      min-width: max-content;
      border: ${(p) => p.theme.borderWidths.none};
    }
  }
`

const headerProps = { $height: '52px' }

const RedesignSelectOrg: FC<{
  org?: Org
  onChange?: (newOrgId: number) => void
}> = ({ onChange, org: orgFromProps }) => {
  const { t } = useIntl()
  const orgFromContext = useOrgContext()
  const [token] = useToken()
  const { farmId } = useParams()
  const [searchParams] = useSearchParams()
  const nav = useNavigate()
  const pathname = usePathname()
  const client = useClient()

  const org = orgFromProps ?? orgFromContext

  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined)
  const { isOpen, toggle, close } = useDisclosure(false)
  const { farmsOptions, isLoading } = useAllUsersOrgs({ enabled: !isNil(token) })

  const matchAdminPath = useMatch('/farms/*/admin/*')

  const isAdmin = !isNil(matchAdminPath)

  const network: RawParentNetwork = useMemo(() => {
    return isNil(org.network)
      ? { id: org.id, name: org.name, logo_url: org.logo }
      : { id: org.network.id, name: org.network.name, logo_url: org.network.logo_url }
  }, [org.id, org.logo, org.name, org.network])

  const selectedOption: OrgOption = useMemo(() => {
    return {
      value: org.id,
      label: org.name,
      networkId: !isNil(network) ? network.id : 0,
      networkName: !isNil(network) ? network.name : '',
      isFarmInNetwork: !isNil(network),
      isMainNetworkFarm: !isNil(org.networkType)
    }
  }, [org.id, org.name, org.networkType, network])

  const searchInputId = `searchQueryFor${org.name}`
  const autocompletePlaceholder = t('actions.search_farm')

  const filteredOptions = useMemo(
    () =>
      farmsOptions.filter(
        (option) =>
          isNil(searchQuery) || option.label.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [farmsOptions, searchQuery]
  )

  const hasOnlyOneChoice = farmsOptions.length <= 1

  const prefetchAndNav = useCallback(
    async (newOrgId: number) => {
      const requester = client.orgs.get(newOrgId)
      await queryClient.prefetchQuery({
        queryKey: requester.key,
        queryFn: requester.request.bind(this)
      })

      onChange?.(newOrgId)

      if (isAdmin && !isNil(farmId)) {
        // Looking for segments with number to only redirect if we are on a details page
        const hasMoreThanTwoNumberSegments =
          pathname.split('/').filter((segment) => !Number.isNaN(parseInt(segment, 10))).length > 1

        nav(
          hasMoreThanTwoNumberSegments
            ? `/farms/${newOrgId}/admin`
            : (pathname.replace(farmId.toString(), newOrgId.toString()) as AbsoluteHref)
        )
      }
      // Always redirecting to the farms home page on org change
      else {
        //Keep the view (list or map) chosen by the user
        nav(`/farms/${newOrgId}`, { search: searchParams })
      }

      close()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client.orgs, close, farmId, isAdmin, onChange, pathname, searchParams]
  )

  return (
    <>
      <FieldContainer
        $isFarmInNetwork={!isNil(network)}
        $isInteractive={!isLoading && !hasOnlyOneChoice}
      >
        {!isNil(network) ? (
          <SelectOrgField onClick={isLoading || hasOnlyOneChoice ? undefined : toggle}>
            <Flex $gap={'md'} $overflow='hidden'>
              <Flex $width={24}>
                {isNil(network?.logo_url) || isEmpty(network.logo_url) ? (
                  <LogoSphere height='100%' width={24} />
                ) : (
                  <NetworkLogo src={network.logo_url} />
                )}
              </Flex>
              <OrgText>{selectedOption.networkName}</OrgText>
            </Flex>
          </SelectOrgField>
        ) : null}
        <SelectOrgField onClick={isLoading || hasOnlyOneChoice ? undefined : toggle}>
          <Flex $gap={'md'} $overflow='hidden'>
            {selectedOption.value === selectedOption.networkId ? (
              <Icons.CertifiedWarehouse $size='xl' />
            ) : (
              <Icons.Warehouse $size='xl' />
            )}
            <OrgText>{selectedOption.label}</OrgText>
          </Flex>
          {isLoading ? (
            <LoadingCircle />
          ) : hasOnlyOneChoice ? null : (
            <Icons.Unfold $size='lg' $color={'grayscale.black'} />
          )}
        </SelectOrgField>
      </FieldContainer>
      {hasOnlyOneChoice ? null : (
        <Modal
          isOpen={isOpen}
          close={close}
          title={t('actions.change_farm')}
          $noInnerPadding
          headerProps={headerProps}
          hasCloseIcon
          width={480}
          height={540}
        >
          <SearchField>
            <Icons.Search $size='md' />
            <InvisibleTextInput
              shouldFocusOnMount
              id={searchInputId}
              name={searchInputId}
              autoComplete='on'
              autoCorrect='off'
              type='text'
              value={searchQuery}
              onChange={(e) => {
                if (!isNil(e.currentTarget)) setSearchQuery(e.currentTarget.value)
              }}
              placeholder={autocompletePlaceholder}
            />
          </SearchField>
          <FarmsList
            farms={filteredOptions}
            onFarmSelection={prefetchAndNav}
            selectedFarm={selectedOption}
            isLoading={isLoading}
            searchQuery={searchQuery}
          />
        </Modal>
      )}
    </>
  )
}

export default RedesignSelectOrg
